package dev.lingvo.site.pages.app.projects.slug

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.letterSpacing
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.ProjectDetailDto
import dev.lingvo.shared.ReadinessDto
import dev.lingvo.shared.WorkspaceDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Session
import dev.lingvo.site.components.ReadinessBand
import dev.lingvo.site.components.release.ReviewedCurrentReleaseCheck
import dev.lingvo.site.ds.IconSize
import dev.lingvo.site.ds.LCircleCheckIcon
import dev.lingvo.site.ds.LSectionHeading
import dev.lingvo.site.ds.LWarningIcon
import dev.lingvo.site.ds.LoadingState
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.iconSize
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.state.ProjectActionKey
import dev.lingvo.site.state.ProjectAuthority
import dev.lingvo.site.state.VerifiedProjectRoute
import dev.lingvo.site.state.liveProjectRoute
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * How many languages are still in the way, said in the heading rather than only in the rows.
 *
 * Counts, never a percentage: "83% ready" is a number nobody can act on, while "2 languages are
 * blocking" names the size of the remaining job. Null when nothing has loaded yet — the heading
 * must not claim readiness it has not been told about.
 */
internal fun readinessBlockingLabel(readiness: ReadinessDto?): String? {
    val locales = readiness?.locales ?: return null
    if (locales.isEmpty()) return null
    val blocking = locales.count { !it.shippable }
    return if (blocking == 0) {
        tr("app.projects.slug.project_readiness_section.release_ready", "Release-ready")
    } else {
        tPlural(
            "app.projects.slug.project_readiness_section.count.blocking",
            blocking,
            "{count} language blocking",
            "{count} languages blocking",
        )
    }
}

/** The overview's exact readiness branch, isolated from unrelated terminal, file, and activity UI. */
@Composable
internal fun ProjectReadinessSection(
    project: ProjectDetailDto,
    authority: ProjectAuthority,
    actionKey: ProjectActionKey,
    refreshSignal: Int,
    currentRoute: () -> VerifiedProjectRoute? = { liveProjectRoute(project.slug) },
    currentMemberships: () -> List<WorkspaceDto> = { Session.organizations },
    transport: suspend (projectSlug: String) -> ReadinessDto = ApiClient::readiness,
    /** Lets the page header state the same answer this section's rows do, from the one request. */
    onLoaded: (ReadinessDto) -> Unit = {},
    onNavigate: (String) -> Unit,
) {
    // **The heading, then the bars.** A mismatched action key used to return nothing at all, so the
    // question this page exists to answer disappeared with no statement that it was ever asked. The
    // heading is true whatever the band is doing, and two skeleton rows say a request is in flight.
    var readiness by remember(actionKey, authority.role) { mutableStateOf<ReadinessDto?>(null) }
    val settled = readiness
    val shippable = settled?.locales?.all { it.shippable } == true
    Column(Modifier.fillMaxWidth().gap(Space.LG)) {
        SectionHeading(
            title = t("app.projects.slug.project_readiness_section.release_readiness", "Release readiness"),
            description = t(
                "app.projects.slug.project_readiness_section.what_is_left_before_this_project",
                "What is left before this project can ship.",
            ),
            // The heavier section on the page, and the glyph says which answer the numbers gave
            // before any of them are read.
            emphasis = true,
            icon = { if (shippable) LCircleCheckIcon() else LWarningIcon() },
            // **No status here.** The page header's badge already carries
            // `readinessBlockingLabel` — it is the page's state line — and appending it to this
            // description put "4 languages blocking" 120px below itself, with the server's summary
            // sentence saying it a third time immediately underneath.
        )
        if (actionKey.projectId != project.id || actionKey.routeSlug != project.slug) {
            // A routing transient, not a retired authority: the action key belongs to the project
            // this page is leaving. Two skeleton rows say a request is coming rather than leaving
            // the section's one question with nothing under it.
            LoadingState(rows = 2, label = t("app.projects.slug.project_readiness_section.checking_release_readiness", "Checking release readiness"))
        } else {
            ReadinessBand(
                key = actionKey,
                authority = authority,
                framed = false,
                refreshSignal = refreshSignal,
                currentRoute = currentRoute,
                currentMemberships = currentMemberships,
                transport = transport,
                onLoaded = {
                    readiness = it
                    onLoaded(it)
                },
                onNavigate = onNavigate,
            )
            ReviewedCurrentReleaseCheck(project, authority, actionKey, currentRoute, currentMemberships, onNavigate)
        }
    }
}

/**
 * A page-section label rather than a second page headline.
 *
 * `emphasis` picks between the two weights the overview needs: the readiness block is the question
 * the page exists to answer and gets the design system's own `LSectionHeading`, while the terminal,
 * files and activity blocks stay the compact tracked label that does not compete with it.
 */
@Composable
internal fun SectionHeading(
    title: String,
    description: String? = null,
    icon: (@Composable () -> Unit)? = null,
    emphasis: Boolean = false,
    actions: (@Composable () -> Unit)? = null,
) {
    if (emphasis) {
        LSectionHeading(
            title = title,
            // The section says what it is; the page header's badge says how it is going. This used
            // to append a `status` clause too, which restated the header's own badge one screenful
            // below it.
            description = description.orEmpty(),
            icon = icon,
            actions = actions,
        )
        return
    }
    Column(Modifier.gap(Space.XXS)) {
        Row(Modifier.gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
            if (icon != null) {
                Span(
                    Modifier
                        .iconSize(IconSize.MD)
                        .color(ColorMode.current.toSitePalette().textSecondary)
                        .styleModifier { property("display", "inline-flex") }
                        .toAttrs { attr("aria-hidden", "true") },
                ) { icon() }
            }
            H2(
                Modifier
                    .color(ColorMode.current.toSitePalette().textSecondary)
                    .fontSize(TypeScale.Small)
                    .fontWeight(600)
                    .letterSpacing(TypeScale.MicroTracking)
                    .margin(0.px)
                    .styleModifier { property("text-transform", "uppercase") }
                    .toAttrs(),
            ) {
                Text(title)
            }
            if (actions != null) actions()
        }
        if (description != null) {
            Span(
                Modifier
                    .color(ColorMode.current.toSitePalette().textMuted)
                    .fontSize(TypeScale.Small)
                    .toAttrs(),
            ) { Text(description) }
        }
    }
}
